import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import {
  fetchLoanKycApproval,
  fetchLoanKycDocuments,
  approveLoanKycDoc,
  rejectLoanKycDoc,
} from "../redux/slices/LoanKycApprovalSlice";
import "../css/LoanKycApproval.css";

const LoanKycApproval = () => {
  const { loanId } = useParams();
  const dispatch = useDispatch();
  const { loan, kycDocs, loading, error } = useSelector(
    (state) => state.loanKycApproval
  );

  // Track loading per document
  const [docLoading, setDocLoading] = useState({});

  // Fetch loan details and KYC documents
  useEffect(() => {
    if (loanId) {
      dispatch(fetchLoanKycApproval(loanId)).then((res) => {
        const userId = res?.payload?.userId;
        if (userId) {
          dispatch(fetchLoanKycDocuments(userId));
        }
      });
    }
  }, [dispatch, loanId]);

  // Approve a document
  const handleApprove = async (docId) => {
    try {
      setDocLoading((prev) => ({ ...prev, [docId]: true }));
      await dispatch(approveLoanKycDoc(docId)).unwrap();
    } catch (err) {
      console.error("Approve failed:", err);
    } finally {
      setDocLoading((prev) => ({ ...prev, [docId]: false }));
    }
  };

  // Reject a document
  const handleReject = async (docId) => {
    try {
      setDocLoading((prev) => ({ ...prev, [docId]: true }));
      await dispatch(rejectLoanKycDoc(docId)).unwrap();
    } catch (err) {
      console.error("Reject failed:", err);
    } finally {
      setDocLoading((prev) => ({ ...prev, [docId]: false }));
    }
  };

  if (loading) return <div className="LoanKycApproval-container">Loading...</div>;

  if (error)
    return (
      <div className="LoanKycApproval-container">
        Error: {typeof error === "string" ? error : JSON.stringify(error)}
      </div>
    );

  if (!loan) return <div className="LoanKycApproval-container">No loan data found.</div>;

  return (
    <div className="LoanKycApproval-container">
      {/* Loan Details */}
      <section className="LoanKycApproval-section">
        <h2 className="LoanKycApproval-title">Loan Details and Loan KYC</h2>
        <p className="LoanKycApproval-subtitle">Know the loan.</p>
        <div className="LoanKycApproval-tableWrap">
          <table className="LoanKycApproval-table">
            <tbody>
              <tr>
                <td>Loan Account</td>
                <td><strong>{loan.loanAccountNumber || "N/A"}</strong></td>
              </tr>
              <tr>
                <td>Loan Amount</td>
                <td><strong>₹{loan.fixedLoanAmount || "0"}</strong></td>
              </tr>
              <tr>
                <td>Interest Amount </td>
                <td><strong>₹{loan.loanInterest || "N/A"}</strong></td>
              </tr>
              <tr>
                <td>ROI Type</td>
                <td><strong>{loan.loanRoiType || "N/A"}</strong></td>
              </tr>
              <tr>
                <td>Loan Tenure</td>
                <td><strong>{loan.fixedLoanTenure || "N/A"} months</strong></td>
              </tr>
              <tr>
                <td>Recovery Frequency</td>
                <td><strong>{loan.fixedLoanFrequency || "N/A"}</strong></td>
              </tr>
              <tr>
                <td>Closure Amount</td>
                <td><strong>₹{loan.loanPayable || "0"}</strong></td>
              </tr>
              <tr>
                <td>Applied Date</td>
                <td>
                  <strong>{loan.appliedDate ? new Date(loan.appliedDate).toLocaleDateString() : "N/A"}</strong>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      {/* Customer Details */}
      <section className="LoanKycApproval-section">
        <h2 className="LoanKycApproval-title">Customer Details</h2>
        <p className="LoanKycApproval-subtitle">Know the customer.</p>
        <div className="LoanKycApproval-tableWrap">
          <table className="LoanKycApproval-table">
            <tbody>
              <tr>
                <td>User Id</td>
                <td><strong>{loan.userId || "N/A"}</strong></td>
              </tr>
              <tr>
                <td>Name</td>
                <td><strong>{loan.userName || "N/A"}</strong></td>
              </tr>
              <tr>
                <td>Aadhar Number</td>
                <td><strong>{loan.userAadharNumber || "N/A"}</strong></td>
              </tr>
              <tr>
                <td>PAN Number</td>
                <td><strong>{loan.userPanNumber || "N/A"}</strong></td>
              </tr>
              <tr>
                <td>Guardian Number</td>
                <td><strong>{loan.guardianMobile || "N/A"}</strong></td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      {/* KYC Documents */}
      <section className="LoanKycApproval-section">
        <h2 className="LoanKycApproval-title">Uploaded Documents</h2>
        <p className="LoanKycApproval-subtitle">KYC verification documents.</p>
        <div className="LoanKycApproval-tableWrap">
          <table className="LoanKycApproval-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Document Name</th>
                <th>See Document</th>
                <th>Document Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {Array.isArray(kycDocs) && kycDocs.length > 0 ? (
                kycDocs.map((doc, idx) => (
                  <tr key={doc.id || idx}>
                    <td>{idx + 1}</td>
                    <td>{doc.docName || "N/A"}</td>
                    <td>
                      {doc.docImage ? (
                        <a
                          href={`${process.env.REACT_APP_API_URL}/uploads/${doc.docImage}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="LoanKycApproval-button"
                        >
                          View
                        </a>
                      ) : (
                        "No file"
                      )}
                    </td>
                    <td>
                      <span className={`LoanKycApproval-status-${doc.status?.toLowerCase()}`}>
                        {doc.status || "Pending"}
                      </span>
                    </td>
                    <td className="LoanKycApproval-actionBtns">
                      <button
                        className="LoanKycApproval-button-approve"
                        onClick={() => handleApprove(doc.id)}
                        disabled={doc.status?.toLowerCase() === "approved" || docLoading[doc.id]}
                      >
                        {docLoading[doc.id] && doc.status?.toLowerCase() !== "approved"
                          ? "Approving..."
                          : "Approve"}
                      </button>
                      <button
                        className="LoanKycApproval-button-reject"
                        onClick={() => handleReject(doc.id)}
                        disabled={doc.status?.toLowerCase() === "rejected" || docLoading[doc.id]}
                      >
                        {docLoading[doc.id] && doc.status?.toLowerCase() !== "rejected"
                          ? "Rejecting..."
                          : "Reject"}
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5">No documents found.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* Guarantor Details */}
      <section className="LoanKycApproval-section">
        <h2 className="LoanKycApproval-title">Loan Guarantor Details</h2>
        <p className="LoanKycApproval-subtitle">Know the loan guarantor.</p>
        <div className="LoanKycApproval-tableWrap">
          <table className="LoanKycApproval-table">
            <tbody>
              <tr>
                <td>Name</td>
                <td><strong>{loan.guarantorName || "N/A"}</strong></td>
              </tr>
              <tr>
                <td>Aadhar</td>
                <td><strong>{loan.guarantorAadhar || "N/A"}</strong></td>
              </tr>
              <tr>
                <td>PAN</td>
                <td><strong>{loan.guarantorPan || "N/A"}</strong></td>
              </tr>
              <tr>
                <td>Relation</td>
                <td><strong>{loan.guarantorRelation || "N/A"}</strong></td>
              </tr>
              <tr>
                <td>Date of Birth</td>
                <td>
                  <strong>
                    {loan.guarantorDob ? new Date(loan.guarantorDob).toLocaleDateString() : "N/A"}
                  </strong>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
};

export default LoanKycApproval;
